import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import {
  Sparkles, Scissors, Palette, Crown, Wind, Brush,
  Hand, Smile, Check, Loader2, ArrowRight, ArrowLeft, Calendar as CalIcon, Clock
} from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { galleryItems } from "@/data/gallery";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIMES = ["09:00", "10:00", "11:00", "12:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

// Find a representative image for each service from the gallery
const findImg = (id) => galleryItems.find((g) => g.id === id)?.src;

const SERVICE_GROUPS = [
  { icon: Sparkles, key: "Balayage", label: "Balayage", desc: "100€ — 200€", featured: true, img: findImg("g47") },
  { icon: Palette, key: "Coloração", label: "Coloração", desc: "26€ — 35€", img: findImg("g13") },
  { icon: Scissors, key: "Corte", label: "Corte", desc: "17€", img: findImg("g38") },
  { icon: Wind, key: "Brushing", label: "Brushing", desc: "10€ — 16€", img: findImg("g15") },
  { icon: Crown, key: "Botox Capilar", label: "Botox / Alisamento", desc: "50€ — 170€", img: findImg("g4") },
  { icon: Brush, key: "Penteado", label: "Penteado", desc: "35€ — 180€", img: findImg("g46") },
  { icon: Hand, key: "Unhas", label: "Unhas / Gel", desc: "18€ — 30€", img: findImg("g39") },
  { icon: Smile, key: "Maquilhagem", label: "Maquilhagem", desc: "30€ — 50€", img: findImg("g42") },
];

const STEPS = ["Serviço", "Data & Hora", "Contacto"];

const Agendamentos = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    service: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const canNext = () => {
    if (step === 0) return !!form.service;
    if (step === 1) return !!date && !!time;
    return true;
  };

  const next = () => canNext() && setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time || !form.service) {
      toast.error("Por favor complete todos os passos.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        notes: form.notes,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
      };
      const res = await axios.post(`${API}/bookings`, payload);
      setConfirmed(res.data);
      toast.success("Pedido de marcação enviado!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      toast.error("Erro ao enviar pedido.");
    } finally {
      setLoading(false);
    }
  };

  if (confirmed) {
    return (
      <div data-testid="booking-confirmation" className="container-luxe py-24 max-w-2xl text-center">
        <div className="w-24 h-24 bg-gold-bright text-ink rounded-full flex items-center justify-center mx-auto shadow-[0_0_50px_rgba(255,215,0,0.5)]">
          <Check className="w-12 h-12" strokeWidth={2} />
        </div>
        <p className="overline mt-8 mb-4">— Pedido recebido</p>
        <h1 className="font-serif text-5xl text-ink">
          Até <span className="italic gold-text">breve</span>, {confirmed.name.split(" ")[0]}.
        </h1>
        <p className="mt-6 text-ink/70 leading-relaxed max-w-md mx-auto">
          O seu pedido foi recebido. Entrarei em contacto consigo nas próximas
          horas para confirmar a sessão.
        </p>
        <div className="mt-8 inline-block bg-beige-light p-6 text-left border-l-2 border-gold">
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="overline mb-1">Serviço</p>
              <p className="font-serif text-lg text-ink">{confirmed.service}</p>
            </div>
            <div>
              <p className="overline mb-1">Data</p>
              <p className="font-serif text-lg text-ink">{confirmed.preferred_date}</p>
            </div>
            <div>
              <p className="overline mb-1">Hora</p>
              <p className="font-serif text-lg text-gold">{confirmed.preferred_time}</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            setConfirmed(null);
            setStep(0);
            setForm({ service: "", name: "", email: "", phone: "", notes: "" });
            setDate(undefined);
            setTime("");
          }}
          data-testid="booking-new-btn"
          className="btn-outline-gold mt-12"
        >
          Fazer novo pedido
        </button>
      </div>
    );
  }

  return (
    <div data-testid="agendamentos-page" className="pb-24 overflow-x-clip">
      <section className="container-luxe py-12">
        <Reveal>
          <p className="overline mb-4">— Agendar</p>
          <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl font-light leading-[0.95] text-ink max-w-3xl">
            A sua <span className="italic gold-text">marcação</span> em 3 passos
          </h1>
          <p className="mt-6 text-ink/70 max-w-xl text-lg">
            Escolha o serviço, dia e hora. Confirmo por telefone ou WhatsApp.
          </p>
        </Reveal>
      </section>

      {/* PROGRESS BAR */}
      <section className="container-luxe">
        <div className="flex items-center gap-4 mb-12 max-w-3xl" data-testid="booking-progress">
          {STEPS.map((label, idx) => (
            <div key={label} className="flex items-center gap-4 flex-1 last:flex-initial">
              <div className="flex items-center gap-3">
                <span
                  className={`w-10 h-10 flex items-center justify-center font-serif text-lg transition-all duration-500 ${
                    idx < step
                      ? "bg-gold text-ink"
                      : idx === step
                      ? "bg-gold-bright text-ink shadow-[0_0_25px_rgba(255,215,0,0.5)]"
                      : "bg-white text-ink/40 border border-ink/15"
                  }`}
                >
                  {idx < step ? <Check className="w-4 h-4" /> : idx + 1}
                </span>
                <span className={`text-xs uppercase tracking-[0.2em] font-medium hidden sm:inline ${
                  idx <= step ? "text-ink" : "text-ink/40"
                }`}>
                  {label}
                </span>
              </div>
              {idx < STEPS.length - 1 && (
                <span className={`flex-1 h-px transition-colors duration-500 ${
                  idx < step ? "bg-gold" : "bg-ink/15"
                }`} />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* STEP CONTENT */}
      <section className="container-luxe">
        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-12" data-testid="booking-form">
          <div className="lg:col-span-8 bg-white p-8 lg:p-12 border border-ink/10 shadow-[0_30px_60px_-30px_rgba(44,44,44,0.15)]">
            {/* STEP 1 — SERVICE */}
            {step === 0 && (
              <Reveal>
                <p className="overline mb-3">Passo 1</p>
                <h2 className="font-serif text-3xl lg:text-4xl text-ink mb-8">
                  Que serviço pretende?
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {SERVICE_GROUPS.map((s) => {
                    const Icon = s.icon;
                    const active = form.service === s.key;
                    return (
                      <button
                        type="button"
                        key={s.key}
                        onClick={() => setForm({ ...form, service: s.key })}
                        data-testid={`booking-service-${s.key.toLowerCase().replace(/\s/g, "-")}`}
                        className={`relative p-5 text-left transition-all duration-300 group flex items-center gap-4 ${
                          active
                            ? "bg-ink text-beige shadow-[0_15px_40px_-10px_rgba(44,44,44,0.4)]"
                            : "bg-beige-light border border-ink/8 hover:border-gold hover:-translate-y-1"
                        }`}
                      >
                        {s.featured && (
                          <span className="absolute -top-2 -right-2 bg-gold-bright text-ink text-[9px] uppercase tracking-wider px-2 py-0.5 z-10">
                            ★ Especialidade
                          </span>
                        )}

                        {/* Small photo thumbnail */}
                        {s.img ? (
                          <div className="relative w-14 h-14 flex-shrink-0 overflow-hidden">
                            <img
                              src={s.img}
                              alt=""
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                              loading="lazy"
                            />
                            <div className={`absolute inset-0 ${active ? "bg-gold/30" : "bg-ink/0 group-hover:bg-gold/10"} transition-colors`} />
                          </div>
                        ) : (
                          <div className={`w-14 h-14 flex items-center justify-center flex-shrink-0 ${active ? "bg-gold-bright" : "bg-beige border border-gold/30"}`}>
                            <Icon className={active ? "text-ink" : "text-gold"} strokeWidth={1.5} />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="font-serif text-lg leading-tight">{s.label}</p>
                          <p className={`text-xs mt-0.5 ${active ? "text-gold" : "text-ink/50"}`}>{s.desc}</p>
                        </div>

                        {active && (
                          <Check className="w-4 h-4 text-gold flex-shrink-0" strokeWidth={2} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </Reveal>
            )}

            {/* STEP 2 — DATE + TIME */}
            {step === 1 && (
              <Reveal>
                <p className="overline mb-3">Passo 2</p>
                <h2 className="font-serif text-3xl lg:text-4xl text-ink mb-8">
                  Quando lhe dá jeito?
                </h2>
                <div className="grid lg:grid-cols-5 gap-8">
                  <div className="lg:col-span-3">
                    <p className="overline mb-3 flex items-center gap-2"><CalIcon className="w-3 h-3" /> Data</p>
                    <div className="bg-beige-light p-2 inline-block border border-gold/20">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        locale={pt}
                        disabled={(d) => {
                          const now = new Date();
                          now.setHours(0, 0, 0, 0);
                          return d < now || d.getDay() === 0 || d.getDay() === 1;
                        }}
                        className="rounded-none"
                        data-testid="booking-calendar"
                      />
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <p className="overline mb-3 flex items-center gap-2"><Clock className="w-3 h-3" /> Hora</p>
                    <div className="grid grid-cols-3 gap-2">
                      {TIMES.map((t) => (
                        <button
                          type="button"
                          key={t}
                          onClick={() => setTime(t)}
                          data-testid={`booking-time-${t.replace(":", "")}`}
                          className={`px-3 py-3 text-sm font-medium tabular-nums transition-all duration-300 ${
                            time === t
                              ? "bg-gold-bright text-ink shadow-[0_0_20px_rgba(255,215,0,0.5)]"
                              : "bg-beige-light border border-ink/10 text-ink hover:border-gold hover:text-gold"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    {date && time && (
                      <div className="mt-6 p-4 bg-gold/10 border-l-2 border-gold text-sm">
                        <p className="font-serif text-lg text-ink">
                          {format(date, "EEEE, d 'de' MMMM", { locale: pt })}
                        </p>
                        <p className="text-gold-dark font-medium">às {time}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Reveal>
            )}

            {/* STEP 3 — CONTACT */}
            {step === 2 && (
              <Reveal>
                <p className="overline mb-3">Passo 3</p>
                <h2 className="font-serif text-3xl lg:text-4xl text-ink mb-8">
                  Os seus contactos
                </h2>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="overline block mb-2">Nome completo *</label>
                    <input
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      data-testid="booking-name"
                      className="input-luxe"
                      placeholder="O seu nome"
                    />
                  </div>
                  <div>
                    <label className="overline block mb-2">Telefone *</label>
                    <input
                      name="phone"
                      required
                      value={form.phone}
                      onChange={handleChange}
                      data-testid="booking-phone"
                      className="input-luxe"
                      placeholder="+351 ___ ___ ___"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline block mb-2">Email *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      data-testid="booking-email"
                      className="input-luxe"
                      placeholder="email@exemplo.pt"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline block mb-2">Notas (opcional)</label>
                    <textarea
                      name="notes"
                      rows={3}
                      value={form.notes}
                      onChange={handleChange}
                      data-testid="booking-notes"
                      className="input-luxe resize-none"
                      placeholder="Comprimento, primeira vez, alergias..."
                    />
                  </div>
                </div>
              </Reveal>
            )}

            {/* NAV BUTTONS */}
            <div className="flex justify-between items-center mt-12 pt-8 border-t border-ink/10">
              {step > 0 ? (
                <button
                  type="button"
                  onClick={back}
                  data-testid="booking-back-btn"
                  className="inline-flex items-center gap-2 text-ink/60 hover:text-gold transition-colors text-sm uppercase tracking-[0.18em] font-medium"
                >
                  <ArrowLeft className="w-4 h-4" /> Voltar
                </button>
              ) : (
                <span />
              )}

              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={next}
                  disabled={!canNext()}
                  data-testid="booking-next-btn"
                  className="btn-gold disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Continuar <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit-btn"
                  className="btn-gold disabled:opacity-50"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> A enviar...</>
                  ) : (
                    <>Confirmar Pedido <Check className="w-4 h-4" /></>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* SUMMARY SIDEBAR */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28 lg:self-start">
            <div className="bg-ink text-beige p-8">
              <p className="overline !text-gold mb-4">— Resumo</p>
              <dl className="space-y-4 text-sm">
                <div className="flex justify-between gap-3 pb-3 border-b border-beige/10">
                  <dt className="text-beige/60">Serviço</dt>
                  <dd className="text-beige text-right font-medium">
                    {form.service || <span className="text-beige/30">—</span>}
                  </dd>
                </div>
                <div className="flex justify-between gap-3 pb-3 border-b border-beige/10">
                  <dt className="text-beige/60">Data</dt>
                  <dd className="text-beige text-right font-medium">
                    {date ? format(date, "d 'de' MMM", { locale: pt }) : <span className="text-beige/30">—</span>}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-beige/60">Hora</dt>
                  <dd className="text-gold font-medium tabular-nums">
                    {time || <span className="text-beige/30">—</span>}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="bg-beige-light p-6 border-l-2 border-gold">
              <p className="overline mb-2">— Prefere falar?</p>
              <a href="tel:+351910327990" data-testid="booking-phone-link" className="font-serif text-xl text-gold hover:underline">
                +351 910 327 990
              </a>
              <p className="text-xs text-ink/50 mt-2">Telefone ou WhatsApp · resposta em horas</p>
            </div>
          </aside>
        </form>
      </section>
    </div>
  );
};

export default Agendamentos;
